import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { Input } from '@foam/buoy/src/components/Input/Input';
import { Modal, ModalContent } from '@foam/buoy/src/components/Modal/Modal';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { BrandfetchBrand } from '~/api/brandfetch';
import { useBrandfetchSearch } from '~/hooks/api/useBrandfetchSearch';
import {
  AvailableLogos,
  SearchDropdown,
  SelectedBrandLogos,
  StaticBrandLogos,
  useBrandExperienceModalStore,
} from '~/screens/PageBuilder/modals/BrandExperienceModal';

import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { AnyBlock, Block } from '../../types';

// Interface for individual logo items
export interface BrandLogoItem {
  id: string;
  url: string;
  brandName: string;
}

// Interface for selected logos (block properties)
export interface BrandLogosProperties {
  logos: BrandLogoItem[];
}

export const BrandExperienceModal = () => {
  const { blocks } = usePageBuilderStore();
  const { isOpen, reset, callback } = useBrandExperienceModalStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [logos, setLogos] = useState<BrandLogoItem[]>([]);
  const [selectedBrandForLogos, setSelectedBrandForLogos] =
    useState<BrandfetchBrand | null>(null);

  const { brands, isLoading } = useBrandfetchSearch({
    searchTerm,
    enabled: isOpen,
    minLength: 2,
  });

  const brandLogosBlock = blocks.find(
    (block: AnyBlock) => block.component === 'BrandLogos',
  ) as Block<'BrandLogos'> | undefined;

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
      setLogos([]);
    };
  }, [reset]);

  // Set existing brand logos when modal opens
  useEffect(() => {
    if (isOpen && brandLogosBlock?.properties.logos) {
      setLogos(brandLogosBlock.properties.logos);
    } else if (isOpen) {
      setLogos([]);
    }
  }, [isOpen]);

  const closeModal = useCallback(() => {
    reset();
    setLogos([]);
    setSelectedBrandForLogos(null);
  }, [reset]);

  const handleSave = useCallback(() => {
    callback({ logos });
    closeModal();
  }, [callback, logos, closeModal]);

  const handleResetSearch = useCallback(() => {
    setSearchTerm('');
    setSelectedBrandForLogos(null);
  }, [setSearchTerm]);

  const handleBrandSelect = useCallback(
    (logo: string, name: string) => {
      handleLogoSelect(logo, name);
      setSearchTerm('');
    },
    [setSearchTerm],
  );

  const handleLogoSelect = useCallback(
    (url: string, brandName: string) => {
      setLogos((prevLogos) => {
        // Check if logo already exists
        const exists = prevLogos.some(
          (logo) => logo.url === url && logo.brandName === brandName,
        );
        if (exists) {
          return prevLogos;
        }
        return [
          ...prevLogos,
          {
            id: url,
            url,
            brandName,
          },
        ];
      });
      handleResetSearch();
    },
    [setLogos, handleResetSearch],
  );

  const handleShowAvailableLogos = useCallback((brand: BrandfetchBrand) => {
    setSelectedBrandForLogos(brand);
  }, []);

  const handleSearchTermChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      // Clear selected brand when user starts typing a new search
      if (selectedBrandForLogos) {
        setSelectedBrandForLogos(null);
      }
    },
    [selectedBrandForLogos],
  );

  const handleClearAllLogos = useCallback(() => {
    setLogos([]);
  }, [setLogos]);

  const handleDeleteLogo = useCallback((logoId: string) => {
    setLogos((prevLogos) => prevLogos.filter((logo) => logo.id !== logoId));
  }, []);

  // Check if there are any changes
  const hasChanges = useCallback(() => {
    const originalLogos = brandLogosBlock?.properties.logos || [];
    if (logos.length !== originalLogos.length) return true;

    return logos.some((logo, index) => {
      const originalLogo = originalLogos[index];
      return (
        !originalLogo ||
        logo.id !== originalLogo.id ||
        logo.url !== originalLogo.url ||
        logo.brandName !== originalLogo.brandName
      );
    });
  }, [logos, brandLogosBlock?.properties.logos]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      title="Brand experience"
      size="md"
      primaryAction={{
        label: 'Save',
        onClick: handleSave,
        isDisabled: !hasChanges(),
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: closeModal,
      }}
      tertiaryAction={{
        label: 'Clear all',
        onClick: handleClearAllLogos,
        isDisabled: logos.length === 0,
      }}
    >
      <ModalContent
        text="Add logos of the brands you've worked with to highlight in this media kit."
        contentArea={
          <div className="flex w-full flex-col gap-4">
            <div className="relative">
              <Input
                value={searchTerm}
                onChange={handleSearchTermChange}
                placeholder="Enter a brand name"
                className="h-8 w-full px-3 text-root-size-body-lg leading-root-line-height-body-lg"
                aria-label="Enter a brand name"
              />
              <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center">
                <Icon
                  name={searchTerm.length > 0 ? 'Close' : 'Search'}
                  color="neutral.300"
                  onClick={
                    searchTerm.length > 0 ? handleResetSearch : undefined
                  }
                  className={searchTerm.length > 0 ? 'cursor-pointer' : ''}
                />
              </div>

              {/* SearchDropdown positioned absolutely within search container */}
              {searchTerm && !selectedBrandForLogos && (
                <SearchDropdown
                  searchTerm={searchTerm}
                  brands={brands}
                  isLoading={isLoading}
                  onShowAvailableLogos={handleShowAvailableLogos}
                  onResetSearch={handleResetSearch}
                />
              )}

              {/* AvailableLogos positioned absolutely within search container */}
              {selectedBrandForLogos && (
                <AvailableLogos
                  brandName={selectedBrandForLogos.name}
                  brandDomain={selectedBrandForLogos.domain}
                  logos={logos}
                  onBrandSelect={handleBrandSelect}
                />
              )}
            </div>

            {logos.length > 0 && (
              <SelectedBrandLogos
                logos={logos}
                onDeleteLogo={handleDeleteLogo}
                onReorder={setLogos}
              />
            )}
            {logos.length === 0 && <StaticBrandLogos />}
          </div>
        }
      />
    </Modal>
  );
};
